import React, { useMemo, useRef, useEffect } from 'react';
import * as THREE from 'three';

interface GroupedLineSegmentsProps {
  lines: { start: [number, number, number]; end: [number, number, number] }[];
  color: string;
  opacity: number;
  dashed?: boolean;
  dashSize?: number;
  gapSize?: number;
  lineWidth?: number;
}

export const GroupedLineSegments: React.FC<GroupedLineSegmentsProps> = ({ 
  lines, color, opacity, dashed = false, dashSize = 3, gapSize = 1, lineWidth = 1
}) => {
  const lineRef = useRef<THREE.LineSegments>(null);

  const geomRef = useRef<THREE.BufferGeometry>(null);

  useEffect(() => {
    if (!geomRef.current || lines.length === 0) return;
    const geom = geomRef.current;
    const requiredLength = lines.length * 6;
    
    let arr: Float32Array;
    const posAttr = geom.getAttribute('position');
    let needsNewAttribute = true;
    
    if (posAttr && posAttr.array.length >= requiredLength) {
      arr = posAttr.array as Float32Array;
      needsNewAttribute = false;
    } else {
      arr = new Float32Array(requiredLength);
    }
    
    let i = 0;
    for (const line of lines) {
      arr[i++] = line.start[0];
      arr[i++] = line.start[1];
      arr[i++] = line.start[2];
      arr[i++] = line.end[0];
      arr[i++] = line.end[1];
      arr[i++] = line.end[2];
    }
    
    if (needsNewAttribute) {
      geom.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    } else {
      posAttr.needsUpdate = true;
    }
    geom.setDrawRange(0, lines.length * 2);
  }, [lines]);



  useEffect(() => {
    if (dashed && lineRef.current && lines.length > 0) {
      lineRef.current.computeLineDistances();
    }
  }, [lines, dashed]);

  if (lines.length === 0) return null;

  return (
    <lineSegments ref={lineRef}>
      <bufferGeometry ref={geomRef} />
      {dashed ? (
        <lineDashedMaterial color={color} transparent opacity={opacity} depthWrite={false} toneMapped={false} dashSize={dashSize} gapSize={gapSize} linewidth={lineWidth} />
      ) : (
        <lineBasicMaterial color={color} transparent opacity={opacity} depthWrite={false} toneMapped={false} linewidth={lineWidth} />
      )}
    </lineSegments>
  );
};
